import React, { useState, useRef, useEffect, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, query, onSnapshot, doc } from 'firebase/firestore';
import { useAppContext } from '../AppContext';
import { useSpring, animated } from '@react-spring/web';
import { useTranslation } from 'react-i18next';
import AiToolsButton from './AiToolsButton';
import AiReplyGenerator from './AiReplyGenerator';
import AIGeneratedContentModal from './AIGeneratedContentModal';
import BadgeIconStrip from './BadgeIconStrip';
import RealtimeCommentCount from './RealtimeCommentCount';
import { ReportModal } from './ReportModal'; // Import ReportModal

const CommentCard = ({ comment: initialComment, authorProfile, onReply, entryId, onCommentDeleted, nexusId = null, contentType = 'whisper', parentPath = null, nestingLevel = 0 }) => {
  const { db, appFunctions, setMessage, userId, LucideIcons, badgeConfig, HoverTooltip, showConfirmation, appId, handleUserSelect } = useAppContext();
  const { t } = useTranslation();
  const [isReactMenuOpen, setIsReactMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const reactButtonRef = useRef(null); // Ref for the react button
  const [menuPosition, setMenuPosition] = useState('top'); // 'top' or 'bottom'

  const [optimisticReactions, setOptimisticReactions] = useState(initialComment.reactions || {});
  const [aiModalContent, setAiModalContent] = useState(null);
  const [aiModalTitle, setAiModalTitle] = useState('');
  const [realtimeReplyCount, setRealtimeReplyCount] = useState(initialComment.replyCount || 0);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [isAmplifying, setIsAmplifying] = useState(false);
  const [isAiToolsOpen, setIsAiToolsOpen] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false); // State for ReportModal

  const [props, api] = useSpring(() => ({
    number: initialComment.echoesInvested || 0,
    delay: 200,
    config: { mass: 1, tension: 20, friction: 10 },
  }));

  useEffect(() => {
    api.start({ number: initialComment.echoesInvested || 0 });
  }, [initialComment.echoesInvested, api]);

  const isAnonymous = initialComment.isAnonymous || !authorProfile;
  const defaultAvatar = "https://placehold.co/40x40/AEC6CF/FFFFFF?text=U";
  const isAuthor = initialComment.authorId === userId;

  const handleReactButtonClick = () => {
    if (reactButtonRef.current) {
      const rect = reactButtonRef.current.getBoundingClientRect();
      // If the button is in the top 150px of the viewport, open the menu below it.
      if (rect.top < 150) {
        setMenuPosition('bottom');
      } else {
        setMenuPosition('top');
      }
    }
    setIsReactMenuOpen(!isReactMenuOpen);
  };

  const handleAmplifyClick = () => {
    handleAmplify();
    setIsAmplifying(true);
    setTimeout(() => setIsAmplifying(false), 400); // Reset after animation duration
  };



  useEffect(() => {
    if (!parentPath) return; // parentPath is the full path to this comment document

    const repliesPath = `${parentPath}/replies`;
    const q = query(collection(db, repliesPath));

    console.log(`[CommentCard] Listening for replies at path: ${repliesPath}`);

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setRealtimeReplyCount(snapshot.size);
    }, (error) => {
      console.error("Error fetching real-time reply count:", error);
    });

    return () => unsubscribe();
  }, [db, parentPath]);

  const handleAiReply = (reply) => {
    onReply(reply);
  };

  const handleAnalysisComplete = (toolType, data) => {
    let title = t('commentCard.aiAnalysis');
    let content = "";

    switch (toolType) {
        case 'GET_AURA_INSIGHT':
            title = data.colorName || t('commentCard.auraInsight');
            content = `**${t('commentCard.mood')}:** ${data.mood}\n\n**${t('commentCard.themes')}:** ${data.themes.join(', ')}\n\n**${t('commentCard.affirmation')}:** "${data.affirmation}"`;
            break;
        case 'GET_VIBE':
            title = t('commentCard.conversationVibe');
            content = data.text;
            break;
        default:
            title = t(`commentCard.toolTypes.${toolType.toLowerCase()}`, toolType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()));
            content = data.text;
    }

    setAiModalTitle(title);
    setAiModalContent(content);
  };

  const badgesToDisplay = useMemo(() => {
    if (!authorProfile?.badges || !badgeConfig) return [];
    const priority = [
        'elder_star', 'echo_deity', 'giant_star', 'echo_legend', 'radiant_star', 'echo_champion',
        'ascendant_star', 'echo_magnate', 'community_pillar', 'proto_star', 'echo_weaver',
        'respected_voice', 'the_spark'
    ];
    
    return authorProfile.badges
        .map(badgeId => ({ id: badgeId, ...badgeConfig[badgeId] }))
        .filter(badge => badge.name)
        .sort((a, b) => {
            const indexA = priority.indexOf(a.id);
            const indexB = priority.indexOf(b.id);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });
  }, [authorProfile, badgeConfig]);

  useEffect(() => {
    setOptimisticReactions(initialComment.reactions || {});
  }, [initialComment.reactions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsReactMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAmplify = async () => {
    const amplifyComment = httpsCallable(appFunctions, 'amplifyComment');
    try {
      await amplifyComment({ fullPath: parentPath, contentType: initialComment.parentId ? 'reply' : 'comment' });
      setMessage(t('commentCard.amplifySuccess'));
    } catch (error) {
      setMessage(t('commentCard.amplifyError', { message: error.message }));
    }
  };

    const handleDelete = () => {
      showConfirmation({
          title: t('commentCard.deleteTitle'),
          message: t('commentCard.deleteMessage'),
          onConfirm: async () => {
              const deleteContent = httpsCallable(appFunctions, 'deleteContent');
              try {
                  await deleteContent({ 
                      contentId: initialComment.id, 
                      contentType: initialComment.parentId ? 'reply' : 'comment',
                      fullPath: parentPath
                  });
                  setMessage(t('commentCard.deleteSuccess'));
                  if (onCommentDeleted) {
                      onCommentDeleted(initialComment.id);
                  }
              } catch (error) {
                  setMessage(t('commentCard.deleteError', { message: error.message }));
              }
          }
      });
    };
  const handleReact = async (emoji) => {
    const originalReactions = { ...optimisticReactions };
    
    setOptimisticReactions(prevReactions => {
        const newReactions = { ...prevReactions };
        const allEmojis = ['❤️', '😂', '🤔', '🔥'];
        let userPreviousReaction = null;

        for (const e of allEmojis) {
            if (newReactions[e] && newReactions[e].includes(userId)) {
                userPreviousReaction = e;
                break;
            }
        }

        if (userPreviousReaction && userPreviousReaction !== emoji) {
            newReactions[userPreviousReaction] = newReactions[userPreviousReaction].filter(id => id !== userId);
        }

        if (userPreviousReaction === emoji) {
            newReactions[emoji] = newReactions[emoji].filter(id => id !== userId);
        } else {
            newReactions[emoji] = [...(newReactions[emoji] || []), userId];
        }
        
        return newReactions;
    });

    setIsReactMenuOpen(false);

    try {
        const updateReaction = httpsCallable(appFunctions, 'updateReaction');
        await updateReaction({ 
            reaction: emoji, 
            contentType: initialComment.parentId ? 'reply' : 'comment', 
            fullPath: parentPath,
            authorId: initialComment.authorId,
            entryId: entryId
        });
    } catch (error) {
        setMessage(t('commentCard.reactError', { message: error.message }));
        setOptimisticReactions(originalReactions); // Revert on failure
    }
  };

  const themeClass = authorProfile?.profileTheme ? `comment-card-theme-${authorProfile.profileTheme}` : 'comment-card-theme-default';

  return (
    <>
    {aiModalContent && <AIGeneratedContentModal title={aiModalTitle} content={aiModalContent} onClose={() => setAiModalContent(null)} />}
    {showReportModal && <ReportModal contentId={initialComment.id} contentType="comment" authorId={initialComment.authorId} onClose={() => setShowReportModal(false)} fullPath={parentPath} />}
    <div className="flex items-start space-x-3 w-full">
      <div className="relative" onMouseEnter={() => setIsTooltipVisible(true)} onMouseLeave={() => setIsTooltipVisible(false)}>
        <img
          src={isAnonymous ? defaultAvatar : authorProfile?.photoURL || defaultAvatar}
          alt={isAnonymous ? t('commentCard.anonymous') : authorProfile?.displayName}
          className="w-10 h-10 rounded-full object-cover flex-shrink-0 mt-1 cursor-pointer"
          onClick={() => !isAnonymous && handleUserSelect(authorProfile.id)}
        />
        {isTooltipVisible && !isAnonymous && (
          <div className="absolute bottom-full mb-2 w-32 bg-gray-900 border border-gray-700 rounded-lg shadow-xl p-2 z-10">
            <button onClick={() => handleUserSelect(authorProfile.id)} className="w-full text-left text-sm text-white hover:bg-gray-800 p-1 rounded">
              {t('commentCard.viewProfile')}
            </button>
          </div>
        )}
      </div>
      <div className="flex-1">
        <div className={`bg-gray-800/70 rounded-xl p-3 transition-all duration-300 border ${badgesToDisplay.length > 0 ? 'border-yellow-400/50' : 'border-transparent'} ${themeClass}`}>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-blue-300 text-sm">{isAnonymous ? t('commentCard.anonymous') : authorProfile?.displayName}</p>
              <BadgeIconStrip badges={authorProfile?.badges} />
              {!isAuthor && !isAnonymous && (
                  <button
                      onClick={() => showConfirmation({
                          message: t('userProfile.actions.addToInnerCircleConfirmation') || "Add this user to your Inner Circle?",
                          onConfirm: async () => {
                              try {
                                  const addInnerCircleMember = httpsCallable(appFunctions, 'addInnerCircleMember');
                                  await addInnerCircleMember({ targetUserId: authorProfile.id });
                                  setMessage(t('userProfile.success.addedToInnerCircle') || "Added to Inner Circle");
                              } catch (error) {
                                  setMessage(t('userProfile.errors.addToInnerCircle', { message: error.message }) || `Error: ${error.message}`);
                              }
                          }
                      })}
                      className="ml-1 text-gray-500 hover:text-pink-400 transition-colors p-1 rounded-full hover:bg-pink-500/10"
                      title={t('userProfile.actions.addToInnerCircle') || "Add to Inner Circle"}
                  >
                      <LucideIcons.Heart size={12} />
                  </button>
              )}
            </div>
            <p className="text-xs text-gray-400">{initialComment.timestamp?.toDate().toLocaleString()}</p>
          </div>
          <p className="text-gray-100 mt-2 text-base break-words">{initialComment.content}</p>
        </div>

        <div className="flex items-center justify-between mt-2 pl-2">
            <div className="flex items-center space-x-3 text-xs text-gray-400">
                {nestingLevel < 2 && (
                    <button onClick={() => onReply()} className="font-semibold hover:text-white transition-colors">{t('commentCard.reply', { count: realtimeReplyCount })}</button>
                )}
                {nestingLevel < 2 && <AiReplyGenerator content={initialComment.content} onReplyGenerated={handleAiReply} />}
                <button onClick={handleAmplifyClick} disabled={isAuthor} className={`font-semibold text-yellow-400 hover:text-yellow-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isAmplifying ? 'flame-animation' : ''}`}>{t('commentCard.amplify')}</button>
                <div className="flex items-center space-x-1 text-yellow-400">
                    <LucideIcons.Flame size={16} />
                    <animated.span className="text-xs font-semibold">
                      {props.number.to(n => n.toFixed(0))}
                    </animated.span>
                </div>
                <div className="relative">
                    <button ref={reactButtonRef} onClick={handleReactButtonClick} className="font-semibold hover:text-white transition-colors">{t('commentCard.react')}</button>
                    {isReactMenuOpen && (
                    <div 
                        ref={menuRef} 
                        className={`absolute ${menuPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'} flex space-x-1 p-2 bg-gray-900 border border-gray-700 rounded-lg shadow-xl`}
                        style={{ zIndex: 'var(--z-index-dropdown)' }}
                    >
                        <button onClick={() => handleReact('❤️')} className="p-1 text-xl hover:scale-125 transition-transform">❤️</button>
                        <button onClick={() => handleReact('😂')} className="p-1 text-xl hover:scale-125 transition-transform">😂</button>
                        <button onClick={() => handleReact('🤔')} className="p-1 text-xl hover:scale-125 transition-transform">🤔</button>
                        <button onClick={() => handleReact('🔥')} className="p-1 text-xl hover:scale-125 transition-transform">🔥</button>
                    </div>
                    )}
                </div>
                {isAuthor && (
                  <button onClick={handleDelete} className="font-semibold text-red-500 hover:text-red-400 transition-colors">{t('commentCard.delete')}</button>
                )}
                 <AiToolsButton 
                    content={initialComment.content} 
                    entryId={entryId} 
                    authorId={initialComment.authorId}
                    contextType="comment"
                    onAnalysisComplete={handleAnalysisComplete}
                    isOpen={isAiToolsOpen}
                    setIsOpen={setIsAiToolsOpen}
                />
                <button onClick={() => setShowReportModal(true)} className="font-semibold text-gray-400 hover:text-white transition-colors">{t('commentCard.report')}</button>
            </div>
            <div className="flex items-center space-x-2">
                {Object.entries(optimisticReactions).map(([emoji, userIds]) => (
                userIds.length > 0 && (
                    <div key={emoji} className="flex items-center px-2 py-1 bg-gray-700/80 rounded-full text-xs">
                    <span>{emoji}</span>
                    <span className="ml-1.5 font-semibold text-gray-300">{userIds.length}</span>
                    </div>
                )
                ))}
            </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default CommentCard;